import { useQuery } from '@tanstack/react-query';

import { studentApi } from '@/api';
import { Alert, Badge, Button, Card, Icon, Skeleton } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

import { CountBadge, GapCounts, PartBody } from './GapParts';
import { GAP_PARTS, summarySentence } from './gaps';

/**
 * "Resume gap analysis" for one CV, on its Score & improve page: a summary,
 * then the four parts as collapsible groups -- open where there is something
 * to do, closed (and saying so) where all is clear.
 *
 * Counted by rules on the server, so it costs nothing, the same CV always
 * gives the same answer, and it is fetched on its own: a slow, limited or
 * failed AI review on the same page never hides it, nor it the review.
 */
export function ResumeGapSection({ resumeId, className }) {
  const analysis = useQuery({
    queryKey: ['resume-gaps', resumeId],
    queryFn: () => studentApi.getResumeGapAnalysis(resumeId),
    enabled: Boolean(resumeId),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <section
      id="gap-analysis"
      aria-labelledby="gap-analysis-title"
      className={cn('scroll-mt-20', className)}
    >
      <Card>
        <div className="mb-3 flex items-start gap-3">
          <span
            aria-hidden="true"
            className="bg-brand-50 text-brand-700 ring-brand-100 grid size-9 shrink-0 place-items-center rounded-lg ring-1 ring-inset"
          >
            <Icon name="target" size={17} />
          </span>
          <div className="min-w-0">
            <h2 id="gap-analysis-title" className="text-ink text-base font-semibold">
              Resume gap analysis
            </h2>
            <p className="text-ink-muted mt-0.5 text-[12px]">
              Skills your goal asks for, breaks in your timeline, missing sections and what differs
              from your profile. Free, counted by rules — no AI.
            </p>
          </div>
        </div>

        {analysis.isPending ? (
          <div className="space-y-2" aria-live="polite">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : analysis.isError ? (
          <Alert tone="error" title="We could not analyse this CV">
            <span className="block">{errorMessage(analysis.error)}</span>
            <Button
              size="sm"
              variant="secondary"
              className="mt-2"
              onClick={() => void analysis.refetch()}
            >
              Try again
            </Button>
          </Alert>
        ) : (
          <GapGroups data={analysis.data} />
        )}
      </Card>
    </section>
  );
}

function GapGroups({ data }) {
  const { summary } = data;
  return (
    <div>
      <div className="bg-surface-muted/50 rounded-input flex flex-wrap items-center justify-between gap-x-4 gap-y-3 p-3">
        <div className="min-w-0 flex-[1_1_16rem]">
          <p className="text-ink text-sm font-medium">{summarySentence(summary)}</p>
        </div>
        <GapCounts summary={summary} skills={data.skills} />
      </div>

      <div
        key={data.resume_id}
        className="divide-line border-line mt-3 divide-y rounded-input border"
      >
        {GAP_PARTS.map((meta) => {
          const part = data[meta.key];
          return (
            <details key={meta.key} open={meta.needsAttention(part)} className="group">
              <summary className="hover:bg-surface-muted/50 flex cursor-pointer list-none items-center gap-3 px-3 py-2.5 [&::-webkit-details-marker]:hidden">
                <span
                  aria-hidden="true"
                  className="bg-surface text-brand-700 ring-line grid size-7 shrink-0 place-items-center rounded-md ring-1 ring-inset"
                >
                  <Icon name={meta.icon} size={14} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="text-ink block text-sm font-semibold">{meta.title}</span>
                  <span className="text-ink-muted block text-xs">{meta.status(part)}</span>
                </span>
                <CountBadge counts={part.counts} />
                <Icon
                  name="chevron"
                  size={14}
                  className="text-ink-muted shrink-0 transition group-open:rotate-90"
                />
              </summary>
              <div className="px-3 pt-1 pb-3">
                <PartBody partKey={meta.key} data={data} />
              </div>
            </details>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-ink-muted text-xs">
          {summary.total === 0 ? (
            <Badge tone="success">Nothing to fix</Badge>
          ) : (
            'Important items first: they are what recruiters notice most.'
          )}
        </p>
      </div>
    </div>
  );
}
